"use client";

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  ChevronRight,
  GalleryVerticalEnd,
  Settings,
  User as UserIcon,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarRail,
} from "@/components/ui/sidebar";
import { dashboardNav } from "@/config/dashboard";
import { useSession } from "next-auth/react";
import {
  usePermissions,
  PermissionLink,
  useJobTitleRefresh,
} from "@/hooks/use-permissions";
import type { NavItem, SubNavItem } from "@/config/dashboard";
import { useSidebarStore } from "@/hooks/use-sidebar-store";
import { useIsMobile } from "@/hooks/use-mobile";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { pusherClient } from "@/lib/pusher";
import { PermissionSet } from "@/types/core/permissions";
import { UserRole } from "@/types/core/auth";

const SidebarMenuItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  const isMobile = useIsMobile();
  const setOpen = useSidebarStore((state) => state.setOpen);

  const handleLinkClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  return (
    <div
      ref={ref}
      data-sidebar='menu-item'
      className={cn("group/menu-item relative", className)}
      onClick={handleLinkClick}
      {...props}
    />
  );
});
SidebarMenuItem.displayName = "SidebarMenuItem";

const SidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => {
  const isMobile = useIsMobile();
  const setOpen = useSidebarStore((state) => state.setOpen);

  const handleLinkClick = () => {
    if (isMobile) {
      setOpen(false);
    }
  };

  return (
    <li
      ref={ref}
      data-sidebar='menu-sub-item'
      className={cn("relative w-full", className)}
      onClick={handleLinkClick}
      {...props}
    />
  );
});
SidebarMenuSubItem.displayName = "SidebarMenuSubItem";

export function AppSidebar({ children }: { children: React.ReactNode }) {
  const { data: session, update: refreshSession } = useSession();
  const { checkPermission } = usePermissions();
  const { jobTitle, isRefreshing } = useJobTitleRefresh();
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const isOpen = useSidebarStore((state) => state.isOpen);
  const setOpen = useSidebarStore((state) => state.setOpen);
  const setHovered = useSidebarStore((state) => state.setHovered);
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);
  const previousItemsRef = useRef<string[]>([]);
  const [userRole, setUserRole] = useState<UserRole | undefined>(
    session?.user?.role
  );

  // Save current state to ref
  useEffect(() => {
    previousItemsRef.current = expandedItems;
  }, [expandedItems]);

  // Update role state when session changes
  useEffect(() => {
    setUserRole(session?.user?.role);
  }, [session?.user?.role]);

  // Handle sidebar open/close
  useEffect(() => {
    if (!isOpen) {
      setExpandedItems([]);
    } else if (previousItemsRef.current.length > 0) {
      setExpandedItems(previousItemsRef.current);
    }
  }, [isOpen]);

  // Listen for role and permission changes from Pusher
  useEffect(() => {
    if (!session?.user?.id) return;

    const channel = pusherClient.subscribe("users");
    const permissionsChannel = pusherClient.subscribe("permissions");

    // Listen for general user updates
    channel.bind("user-updated", async () => {
      await refreshSession();
    });

    // Listen for job title changes
    channel.bind(
      "user-job-changed",
      async (data: {
        userId: string;
        jobTitle: string;
        jobId?: string;
        timestamp?: string;
        type?: "assignment" | "update";
      }) => {
        if (data.userId === session.user.id) {
          try {
            // Add a small delay to ensure database updates have completed
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Update session via NextAuth's update method
            await refreshSession();
          } catch (error) {
            console.error(
              "Error refreshing session after job title change:",
              error
            );
          }
        }
      }
    );

    // Listen for role changes
    channel.bind(
      "user-role-changed",
      async (updatedUser: { id: string; role: string }) => {
        if (updatedUser.id === session.user.id) {
          try {
            // Update session via NextAuth to reflect the new role
            await refreshSession();
            // Update local state to trigger re-render
            setUserRole(updatedUser.role as UserRole);
          } catch (error) {
            console.error("Error refreshing session after role change:", error);
          }
        }
      }
    );

    // Listen for permission updates
    permissionsChannel.bind(
      "permissions-updated",
      async (data: {
        userId: string;
        resourceName: string;
        permissions: PermissionSet;
      }) => {
        if (data.userId === session.user.id) {
          try {
            await refreshSession();
          } catch (error) {
            console.error(
              "Error refreshing session after permissions update:",
              error
            );
          }
        }
      }
    );

    return () => {
      pusherClient.unsubscribe("users");
      pusherClient.unsubscribe("permissions");
    };
  }, [session?.user?.id, refreshSession]);

  // Show sidebar for admin/staff users
  const showSidebar =
    session?.user && (userRole === "admin" || userRole === "staff");

  // Determine if we should render the sidebar layout at all
  const shouldShowSidebarLayout = showSidebar;

  // Add hover handlers
  const handleMouseEnter = React.useCallback(() => {
    if (!isMobile) {
      setHovered(true);
      setOpen(true);
    }
  }, [isMobile, setHovered, setOpen]);

  const handleMouseLeave = React.useCallback(() => {
    if (!isMobile) {
      setHovered(false);
      setOpen(false);
    }
  }, [isMobile, setHovered, setOpen]);

  // Check if user has any permissions set to true
  const hasNoPermissions =
    userRole !== "admin" && // Bypass check for admin users
    (!session?.user?.permissions ||
      !Object.values(session.user.permissions).some(
        (permissionObj) =>
          permissionObj.create ||
          permissionObj.read ||
          permissionObj.update ||
          permissionObj.delete
      ));

  // Check if there's a valid job title to display, either from the hook or from the session
  const displayJobTitle = jobTitle || session?.user?.jobTitle;
  const isLoading = hasNoPermissions || (!displayJobTitle && isRefreshing);

  if (!showSidebar) {
    return <div className='w-full'>{children}</div>;
  }

  // On mobile or when sidebar shouldn't be shown, render without sidebar layout
  if (!shouldShowSidebarLayout) {
    return <div className='w-full'>{children}</div>;
  }

  // Helper function to check if a nav item should be visible
  const shouldShowNavItem = (item: NavItem | SubNavItem): boolean => {
    // Admin override - admins can see everything
    if (userRole === "admin") return true;

    // If no permission is required, show the item
    if (!item.permission) return true;

    // Check permission through the permissions hook
    return checkPermission(item.permission.resource, item.permission.action);
  };

  // Helper function to check if any items in a group are visible
  const hasVisibleItems = (items: NavItem[]) => {
    return items.some((item) => {
      // Check if the item itself is visible
      if (shouldShowNavItem(item)) {
        // If the item has sub-items, check if any of them are visible
        if (item.items) {
          return item.items.some((subItem) => shouldShowNavItem(subItem));
        }
        return true;
      }
      return false;
    });
  };

  const renderNavItems = (items: NavItem[]) => {
    // Filter out items that shouldn't be visible based on permissions
    return items
      .filter(shouldShowNavItem)
      .map((item) => {
        if (item.items) {
          // For items with sub-items, check if any sub-items should be visible
          const visibleSubItems = item.items.filter(shouldShowNavItem);

          // If no sub-items are visible, don't render the parent either
          if (visibleSubItems.length === 0) return null;

          const isExpanded = expandedItems.includes(item.title);

          return (
            <Collapsible
              key={item.title}
              open={isExpanded}
              onOpenChange={(open) => {
                setExpandedItems((prev) =>
                  open
                    ? [...prev, item.title]
                    : prev.filter((title) => title !== item.title)
                );
              }}
              className='group/collapsible'
            >
              <CollapsibleTrigger asChild>
                <SidebarMenuButton
                  className={
                    visibleSubItems.some((subItem) => subItem.url === pathname)
                      ? "bg-primary text-primary-foreground"
                      : ""
                  }
                >
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {visibleSubItems.map((subItem) => (
                    <SidebarMenuSubItem key={subItem.title}>
                      <SidebarMenuSubButton
                        asChild
                        className={
                          pathname === subItem.url
                            ? "bg-primary text-primary-foreground"
                            : ""
                        }
                      >
                        {subItem.permission ? (
                          <PermissionLink
                            resource={subItem.permission.resource}
                            action={subItem.permission.action}
                            href={subItem.url}
                          >
                            <span>{subItem.title}</span>
                          </PermissionLink>
                        ) : (
                          <Link href={subItem.url}>
                            <span>{subItem.title}</span>
                          </Link>
                        )}
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </Collapsible>
          );
        }

        return (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton
              asChild
              className={
                pathname === item.url
                  ? "bg-primary text-primary-foreground"
                  : ""
              }
            >
              {item.permission ? (
                <PermissionLink
                  resource={item.permission.resource}
                  action={item.permission.action}
                  href={item.url ?? ""}
                >
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </PermissionLink>
              ) : (
                <Link href={item.url ?? ""}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                </Link>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })
      .filter(Boolean); // Filter out null items
  };

  // On mobile, render content without sidebar layout constraints
  if (isMobile) {
    return (
      <div className='w-full min-h-screen'>
        <Sidebar
          collapsible='icon'
          className='flex border-mute z-[99] overflow-hidden'
          open={isOpen}
          onOpenChange={setOpen}
        >
          <SidebarHeader className='shrink-0'>
            {isLoading ? (
              <div className='flex items-center align-middle gap-4'>
                <div className='flex aspect-square size-8 items-center justify-center rounded-lg'>
                  <Settings className='w-8 h-8 animate-spin text-primary duration-3000' />
                </div>
                <span className='truncate font-semibold'>Loading</span>
              </div>
            ) : (
              <div className='flex items-center align-middle gap-4'>
                <div className='flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground'>
                  <GalleryVerticalEnd />
                </div>
                <span className='truncate font-semibold'>
                  {displayJobTitle || "Unassigned"}
                </span>
              </div>
            )}
          </SidebarHeader>
          <ScrollArea className='flex-1'>
            <SidebarContent>
              {hasNoPermissions ? (
                <div
                  className={`flex flex-col items-center justify-center p-6 text-center transition-opacity duration-200 ${
                    !isOpen ? "opacity-0" : "opacity-100"
                  }`}
                >
                  <div className='space-y-2'>
                    <h2 className='text-lg font-semibold'>
                      Setting Up Your Dashboard
                    </h2>
                    <p className='text-sm text-muted-foreground'>
                      Things are being set up for your viewing. We will notify
                      you once your dashboard has been configured.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {hasVisibleItems(dashboardNav.navMain) && (
                    <SidebarGroup>
                      <SidebarMenu>
                        {renderNavItems(dashboardNav.navMain)}
                      </SidebarMenu>
                    </SidebarGroup>
                  )}

                  {hasVisibleItems(dashboardNav.role) && (
                    <SidebarGroup>
                      <SidebarGroupLabel>Role</SidebarGroupLabel>
                      <SidebarMenu>
                        {renderNavItems(dashboardNav.role)}
                      </SidebarMenu>
                    </SidebarGroup>
                  )}

                  {hasVisibleItems(dashboardNav.management) && (
                    <SidebarGroup>
                      <SidebarGroupLabel>Management</SidebarGroupLabel>
                      <SidebarMenu>
                        {renderNavItems(dashboardNav.management)}
                      </SidebarMenu>
                    </SidebarGroup>
                  )}

                  {hasVisibleItems(dashboardNav.help) && (
                    <SidebarGroup>
                      <SidebarGroupLabel>Help</SidebarGroupLabel>
                      <SidebarMenu>
                        {renderNavItems(dashboardNav.help)}
                      </SidebarMenu>
                    </SidebarGroup>
                  )}
                </>
              )}
            </SidebarContent>
          </ScrollArea>
          <SidebarFooter className='shrink-0 border-t border-muted p-0'>
            <SidebarGroup className='py-2'>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link href='/account'>
                      <UserIcon />
                      <span>My Account</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroup>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>
        <div className='w-full max-w-full overflow-x-hidden'>{children}</div>
      </div>
    );
  }

  // Desktop layout with flex structure
  return (
    <div className='w-full flex h-[calc(100dvh-4rem)]'>
      <Sidebar
        collapsible='icon'
        className='flex border-mute z-[99] overflow-hidden'
        open={isOpen}
        onOpenChange={setOpen}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <SidebarHeader className='shrink-0'>
          {isLoading ? (
            <div className='flex items-center align-middle gap-4'>
              <div className='flex aspect-square size-8 items-center justify-center rounded-lg'>
                <Settings className='w-8 h-8 animate-spin text-primary duration-3000' />
              </div>
              <span className='truncate font-semibold'>Loading</span>
            </div>
          ) : (
            <div className='flex items-center align-middle gap-4'>
              <div className='flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground'>
                <GalleryVerticalEnd />
              </div>
              <span className='truncate font-semibold'>
                {displayJobTitle || "Unassigned"}
              </span>
            </div>
          )}
        </SidebarHeader>
        <ScrollArea className='flex-1'>
          <SidebarContent>
            {hasNoPermissions ? (
              <div
                className={`flex flex-col items-center justify-center p-6 text-center transition-opacity duration-200 ${
                  !isOpen ? "opacity-0" : "opacity-100"
                }`}
              >
                <div className='space-y-2'>
                  <h2 className='text-lg font-semibold'>
                    Setting Up Your Dashboard
                  </h2>
                  <p className='text-sm text-muted-foreground'>
                    Things are being set up for your viewing. We will notify you
                    once your dashboard has been configured.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {hasVisibleItems(dashboardNav.navMain) && (
                  <SidebarGroup>
                    <SidebarMenu>
                      {renderNavItems(dashboardNav.navMain)}
                    </SidebarMenu>
                  </SidebarGroup>
                )}

                {hasVisibleItems(dashboardNav.role) && (
                  <SidebarGroup>
                    <SidebarGroupLabel>Role</SidebarGroupLabel>
                    <SidebarMenu>
                      {renderNavItems(dashboardNav.role)}
                    </SidebarMenu>
                  </SidebarGroup>
                )}

                {hasVisibleItems(dashboardNav.management) && (
                  <SidebarGroup>
                    <SidebarGroupLabel>Management</SidebarGroupLabel>
                    <SidebarMenu>
                      {renderNavItems(dashboardNav.management)}
                    </SidebarMenu>
                  </SidebarGroup>
                )}

                {hasVisibleItems(dashboardNav.help) && (
                  <SidebarGroup>
                    <SidebarGroupLabel>Help</SidebarGroupLabel>
                    <SidebarMenu>
                      {renderNavItems(dashboardNav.help)}
                    </SidebarMenu>
                  </SidebarGroup>
                )}
              </>
            )}
          </SidebarContent>
        </ScrollArea>
        <SidebarFooter className='shrink-0 border-t border-muted p-0'>
          <SidebarGroup className='py-2'>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <Link href='/account'>
                    <UserIcon />
                    <span>My Account</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <ScrollArea className='flex-1 h-full'>
        <div className='w-full'>{children}</div>
      </ScrollArea>
    </div>
  );
}
